import { Injectable, ViewContainerRef } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { ModalService } from '../modal/modal.service';
import { RessourceService } from '../ressource/ressource.service';
import { take } from 'rxjs/operators';
import { EdcChoixActiviteComponent } from 'src/app/fragments/modal/edc-choix-activite/edc-choix-activite.component';

@Injectable({
  providedIn: 'root'
})
export class EdcAuthService {
  /**
   * Jeton de corrélation Pro Santé Connect (PSC) pour le proxy Espace de Confiance (EdC).
   * Ce token permet de lier la session de l'utilisateur avec le proxy d'iCanopée.
   */
  public s_correlationId: string = 'test-correlation-id'; // Valeur par défaut pour le développement
  public practice_setting: string;
  public user_name: string;
  public user_given: string;
  public id_nat_ps: string;
  /**
   * Activité PSC sélectionnée lors de la connexion (cadre d'exercice courant du PS).
   */
  public selectedActivity: any;
  /**
   * Exercice PSC du PS (identité et qualification professionnelle : nom/prénom d'exercice, profession, spécialité).
   * On part du principe qu'un utilisateur n'a qu'un seul exercice.
   */
  public exercice: any;
  /**
   * Rôles du PS retournés par PSC (ex : "10^1.2.250.1.213.1.1.5.5"), utilisés pour retrouver l'OID de la profession.
   */
  public subjectRole: string[];

  constructor(
    private _http: HttpClient,
    private _modalSrv: ModalService,
    private _resourceSrv: RessourceService
  ) {}

  /**
   * Connecte l'utilisateur via le point d'entrée /connectUser du proxy d'Espace de Confiance en mode CodeFlow.
   */
  public async connectUser(sIdNatPs: string, view?: ViewContainerRef): Promise<any> {
    const currentProfilStr = localStorage.getItem('currentProfil');
    if (!currentProfilStr) {
      throw new Error("Impossible de récupérer les informations de votre profil pour l'authentification.");
    }
    const profil = JSON.parse(currentProfilStr);
    if (!profil || !profil.id) {
      throw new Error("Impossible de récupérer les informations de votre profil pour l'authentification.");
    }

    const userName = profil.utilisateur?.nom;
    if (!userName) {
      throw new Error("Le nom de famille du médecin n'est pas renseigné.");
    }

    const userGiven = profil.utilisateur?.prenom;
    if (!userGiven) {
      throw new Error("Le prénom du médecin n'est pas renseigné.");
    }

    if (!sIdNatPs) {
      throw new Error("Le numéro ID nat du médecin n'est pas renseigné.");
    }

    const params = await this._resourceSrv.getResourceSettings(profil.id, 'profil');
    if (params) {
      const settingObj = this._resourceSrv.getSettingFromArray(params, 'dmp_params_practice_setting');
      if (!settingObj || !settingObj.value) {
        throw new Error("Le cadre d'exercice n'est pas renseigné. Vous devez le renseigner avant toutes choses.");
      }
      this.practice_setting = settingObj.value;
    } else {
      throw new Error("Impossible de charger les paramètres de votre profil.");
    }

    this.user_name = userName;
    this.user_given = userGiven;
    this.id_nat_ps = sIdNatPs;

    try {
      const savedId = await this.getSavedLCorrelationId();
      if (savedId) {
        return { s_correlationId: savedId, s_status: 'OK' };
      }
    } catch (checkError) {
      console.warn('Erreur lors de la vérification de la session EDC persistée:', checkError);
      const existingMapStr = localStorage.getItem('edc_correlationId');
      if (existingMapStr) {
        try {
          const map = JSON.parse(existingMapStr);
          if (map && map[profil.id]) {
            delete map[profil.id];
            localStorage.setItem('edc_correlationId', JSON.stringify(map));
          }
        } catch (e) {}
      }
    }

    const payload = {
      s_idNatPs: sIdNatPs,
      Services: ['dmp', 'insi', 'e-prescription'],
      i_authenticationMode: 2
    };

    const headers = new HttpHeaders().set('X-Target-Path', '/connectUser');

    // Déclencher la redirection dans un nouvel onglet en parallèle de l'appel HTTP
    if ((environment as any).iCanopeeCodeflowConnectionUrl) {
      window.open((environment as any).iCanopeeCodeflowConnectionUrl, '_blank');
    }

    let loading: any;
    if (view && this._modalSrv) {
      loading = this._modalSrv.openLoading(view);
      loading.instance.message = 'En attente de votre authentification Pro Santé Connect...';
    }

    try {
      const response = await this._http.post<any>(environment.edcProxyUrl, payload, { headers }).toPromise();

      // L'attente PSC est terminée : le chargement ne doit pas rester affiché sous la sélection de l'activité
      if (loading) {
        loading.destroy();
        loading = undefined;
      }

      if (response && response.s_correlationId) {
        this.s_correlationId = response.s_correlationId;

        let selectedActivity: any = undefined;
        const exercices = response.RawUserInfos?.SubjectRefPro?.exercices;
        const exercice = (exercices && exercices.length > 0) ? exercices[0] : undefined;
        const subjectRole = response.RawUserInfos?.SubjectRole;
        const activities = exercice ? exercice.activities : undefined;

        if (activities && activities.length > 0) {
          if (activities.length === 1) {
            selectedActivity = activities[0];
          } else if (activities.length > 1) {
            selectedActivity = await this._selectActivity(activities, view);

            if (!selectedActivity) {
              const currentMapStr = localStorage.getItem('edc_correlationId');
              if (currentMapStr) {
                try {
                  const map = JSON.parse(currentMapStr);
                  if (map && map[profil.id]) {
                    delete map[profil.id];
                    localStorage.setItem('edc_correlationId', JSON.stringify(map));
                  }
                } catch (e) {}
              }
              this.s_correlationId = 'test-correlation-id';
              throw new Error("Vous devez obligatoirement sélectionner une activité pour vous connecter.");
            }
          }
        }

        this.selectedActivity = selectedActivity;
        this.exercice = exercice;
        this.subjectRole = subjectRole;

        // Persistance locale sous forme de Map <profilId>:{idNat, correlationId, activity, exercice, subjectRole}
        const currentMapStr = localStorage.getItem('edc_correlationId');
        let map: { [profilId: string]: { idNat: string; correlationId: string; activity: any; exercice: any; subjectRole: string[] } } = {};
        if (currentMapStr) {
          try {
            map = JSON.parse(currentMapStr);
            if (typeof map !== 'object' || map === null) {
              map = {};
            }
          } catch (e) {
            map = {};
          }
        }
        map[profil.id] = {
          idNat: sIdNatPs,
          correlationId: response.s_correlationId,
          activity: selectedActivity,
          exercice,
          subjectRole
        };
        localStorage.setItem('edc_correlationId', JSON.stringify(map));
      }
      return response;
    } catch (error) {
      console.error('Erreur lors de la connexion EDC connectUser:', error);
      throw error;
    } finally {
      if (loading) {
        loading.destroy();
      }
    }
  }

  /**
   * Ouvre la modal de sélection de l'activité (même rendu que la sélection du lieu d'exercice en stratégie websocket).
   * Retourne undefined si l'utilisateur annule ou si aucune vue n'est disponible pour afficher la modal.
   */
  private async _selectActivity(activities: any[], view?: ViewContainerRef): Promise<any> {
    if (!view) {
      return undefined;
    }
    const modal = this._modalSrv.openModal<EdcChoixActiviteComponent>(view, EdcChoixActiviteComponent, [
      { name: 'activities', value: activities }
    ]);
    try {
      return await modal.instance.closeEmitter.pipe(take(1)).toPromise();
    } finally {
      modal.destroy();
    }
  }

  /**
   * Récupère le jeton de corrélation persisté s'il existe et est toujours valide.
   */
  public async getSavedLCorrelationId(): Promise<string | undefined> {
    const currentProfilStr = localStorage.getItem('currentProfil');
    if (!currentProfilStr) {
      return undefined;
    }
    const profil = JSON.parse(currentProfilStr);
    if (!profil || !profil.id) {
      return undefined;
    }

    const existingMapStr = localStorage.getItem('edc_correlationId');
    if (existingMapStr) {
      try {
        const map = JSON.parse(existingMapStr);
        if (map && map[profil.id]) {
          const entry = map[profil.id];
          const persistedCorrelationId = entry.correlationId;

          if (persistedCorrelationId && persistedCorrelationId !== 'test-correlation-id') {
            const checkHeaders = new HttpHeaders()
              .set('X-Target-Path', '/checkUserSession')
              .set('X-User-Name', this.user_name || '')
              .set('X-User-Given', this.user_given || '')
              .set('X-IdNatPs', this.id_nat_ps || '');
            const checkPayload = { s_correlationId: persistedCorrelationId };
            const checkResponse = await this._http.post<any>(environment.edcProxyUrl, checkPayload, { headers: checkHeaders }).toPromise();
            if (checkResponse && checkResponse.s_status === 'OK' && checkResponse.i_valid === 1) {
              this.s_correlationId = persistedCorrelationId;
              this.selectedActivity = entry.activity;
              this.exercice = entry.exercice;
              this.subjectRole = entry.subjectRole;
              return persistedCorrelationId;
            } else {
              delete map[profil.id];
              localStorage.setItem('edc_correlationId', JSON.stringify(map));
            }
          }
        }
      } catch (e) {
        console.warn('Erreur lors de la vérification de la session EDC:', e);
      }
    }
    return undefined;
  }
}
